(() => {
  'use strict';

  const EVENT_NAMES = Object.freeze({
    viewed: 'eversnug_warranty_page_viewed',
    started: 'eversnug_warranty_registration_started',
    submitAttempted: 'eversnug_warranty_registration_submit_attempted',
    submitted: 'eversnug_warranty_registration_submitted',
    storeCtaClicked: 'eversnug_warranty_store_cta_clicked'
  });

  const clean = (value, maximum = 160) => String(value || '').trim().slice(0, maximum);

  const publish = (name, detail = {}) => {
    const payload = { event: name, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, detail);
      }
    } catch (_error) {
      // Registration must never depend on analytics availability.
    }
  };

  const createRegistrationId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();

    const bytes = new Uint8Array(16);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };

  const initialize = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = root.querySelector('form');
    const success = root.querySelector('[data-registration-success]');
    const error = root.querySelector('[data-registration-error]');
    const registrationId = root.querySelector('[data-registration-id]');
    const productRadios = Array.from(root.querySelectorAll('[data-product-radio]'));
    const stylePanels = Array.from(root.querySelectorAll('[data-product-variants]'));
    const colourStyleField = root.querySelector('[data-colour-style-field]');
    const productIdField = root.querySelector('[data-product-id-field]');
    const productHandleField = root.querySelector('[data-product-handle-field]');
    const variantIdField = root.querySelector('[data-variant-id-field]');
    const variantSkuField = root.querySelector('[data-variant-sku-field]');
    let started = false;

    publish(EVENT_NAMES.viewed, { form_version: 'warranty-v2' });

    root.querySelectorAll('[data-store-cta]').forEach((link) => {
      link.addEventListener('click', () => {
        publish(EVENT_NAMES.storeCtaClicked, {
          form_version: 'warranty-v2',
          cta_source: clean(link.dataset.storeCta, 32)
        });
      });
    });

    if (success) {
      success.focus();
      publish(EVENT_NAMES.submitted, { form_version: 'warranty-v2' });
      return;
    }

    if (error) error.focus();
    if (!form) return;

    if (registrationId && !registrationId.value) {
      registrationId.value = createRegistrationId();
    }

    const params = new URLSearchParams(window.location.search);
    form.querySelectorAll('[data-attribution-field]').forEach((field) => {
      const key = field.dataset.attributionField;
      if (key) field.value = clean(params.get(key), 120);
    });

    const syncProductSelection = (radio) => {
      const handle = clean(radio?.dataset?.productHandle, 160);
      if (productIdField) productIdField.value = clean(radio?.dataset?.productId, 40);
      if (productHandleField) productHandleField.value = handle;
      if (colourStyleField) colourStyleField.value = '';
      if (variantIdField) variantIdField.value = '';
      if (variantSkuField) variantSkuField.value = '';

      stylePanels.forEach((panel) => {
        const isActive = Boolean(handle && panel.dataset.productVariants === handle);
        const styleInputs = Array.from(panel.querySelectorAll('[data-style-input]'));

        panel.hidden = !isActive;
        styleInputs.forEach((input) => {
          input.disabled = !isActive;
          input.required = false;
        });

        if (!isActive || !styleInputs.length) return;
        styleInputs[0].required = true;
        const checkedStyle = styleInputs.find((input) => input.checked);
        if (checkedStyle && colourStyleField) colourStyleField.value = clean(checkedStyle.value, 80);
        if (checkedStyle && variantIdField) variantIdField.value = clean(checkedStyle.dataset.variantId, 80);
        if (checkedStyle && variantSkuField) variantSkuField.value = clean(checkedStyle.dataset.variantSku, 80);
      });
    };

    productRadios.forEach((radio) => {
      radio.addEventListener('change', () => syncProductSelection(radio));
    });

    root.querySelectorAll('[data-style-input]').forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked && colourStyleField) colourStyleField.value = clean(input.value, 80);
        if (input.checked && variantIdField) variantIdField.value = clean(input.dataset.variantId, 80);
        if (input.checked && variantSkuField) variantSkuField.value = clean(input.dataset.variantSku, 80);
      });
    });

    syncProductSelection(productRadios.find((radio) => radio.checked));

    const markStarted = (event) => {
      if (started || event.target?.matches?.('.wr-honeypot')) return;
      started = true;
      publish(EVENT_NAMES.started, { form_version: 'warranty-v2' });
    };

    form.addEventListener('focusin', markStarted, { once: true });
    form.addEventListener('change', markStarted, { once: true });

    form.addEventListener('submit', () => {
      const button = form.querySelector('[data-submit-registration]');
      publish(EVENT_NAMES.submitAttempted, {
        form_version: 'warranty-v2',
        product_selected: Boolean(form.elements['contact[Product]']?.value),
        colour_supplied: Boolean(clean(form.elements['contact[Colour or style]']?.value))
      });

      form.setAttribute('aria-busy', 'true');
      if (button) {
        button.disabled = true;
        const label = button.querySelector('[data-submit-label]');
        if (label) label.textContent = 'Registering…';
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      form.removeAttribute('aria-busy');
      const button = form.querySelector('[data-submit-registration]');
      if (button) {
        button.disabled = false;
        const label = button.querySelector('[data-submit-label]');
        if (label) label.textContent = 'Register my warranty';
      }
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-warranty-registration]').forEach(initialize);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-warranty-registration]').forEach(initialize);
  });
})();
